<script lang="ts">
import { computed, defineComponent, PropType } from 'vue';

import { AddressSuggestion } from '@/api/address/autocomplete';

export default defineComponent({
  name: 'AddressSuggestions',
  props: {
    showSuggestions: { required: true, type: Boolean as PropType<boolean> },
    suggestions: { required: true, type: Array as PropType<AddressSuggestion[]> },
  },
  setup(props) {
    const borderUtilities = ['border', 'border-solid', 'border-black'].join(' ');

    const optionUtilities = [
      'hover:bg-neutral-100',
      'cursor-pointer',
      'mr-2',
      'pl-3',
      'pr-2',
      'py-3',
      'relative',
      'select-none',
      'text-black',
    ].join(' ');

    const options = computed<AddressSuggestion[]>(() =>
      props.suggestions.map((option) => {
        const { label: rawText, matched } = option;
        let label = rawText;
        matched.forEach(([start, end]) => {
          const component = label.substring(start, end);
          const alpha = label.substring(0, Math.max(0, start));
          const omega = label.substring(Math.min(label.length, end), label.length);
          label = `${alpha}<strong>${component}</strong>${omega}`;
        });
        const [line1, line2] = label.split(/, ?/);
        label = `${line1}<br /><span class="text-neutral-500">${line2}</span>`;
        return {
          ...option,
          label,
        };
      }),
    );

    return {
      borderUtilities,
      optionUtilities,
      options,
    };
  },
});
</script>

<template>
  <div>
    <slot />
    <div
      v-if="showSuggestions"
      class="absolute bg-white border-t-0 overflow-hidden px-1.5 py-3 shadow-lg w-full z-10"
      :class="borderUtilities"
    >
      <ul class="overflow-auto text-sm leading-5 max-h-56 focus:outline-none styled-scrollbar">
        <li
          v-for="option in options"
          :key="option.label"
          class="border-b border-solid border-neutral-300"
          :class="optionUtilities"
          @click="$emit('select-address', option.value)"
          v-html="option.label"
        />
        <li :class="optionUtilities" @click="$emit('cancel')">Use this address</li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
.styled-scrollbar::-webkit-scrollbar {
  width: 6px;
  height: auto;
  background-color: #dcdcdc;
  border-radius: 9999px;
}

.styled-scrollbar::-webkit-scrollbar-thumb {
  background: #333;
  border-radius: 9999px;
}
</style>
